import { useContext } from 'react';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { ServerErrorResponse, baseApiUrl, fetcher } from 'data_sources/constants';
import { AppContext } from 'views/App/context';
import { PolicyBarChartDataPoint, PolicyBarChartResponse, PolicyTrendsResponse } from '../_types';
import { generatePolicyTrends } from './riskPoolOrgDataGenerator';
import { useRiskPoolDemoData } from '../../../../_data_sources/useRiskPoolDemoData';

dayjs.extend(customParseFormat);

export type PeriodMode = 'weeks' | 'months';

interface QueryParams {
  startDate: string;
  endDate: string;
  manualId?: string;
}

/**
 * Determines period mode from date range: short range (e.g. daily data) → weeks, longer → months.
 * Parses dates as local (YYYY-MM-DD) to avoid UTC shift.
 */
export function getPeriodMode(startDate: string, endDate: string): PeriodMode {
  if (!startDate || !endDate) return 'months';
  const start = dayjs(startDate, 'YYYY-MM-DD', true);
  const end = dayjs(endDate, 'YYYY-MM-DD', true);
  if (!start.isValid() || !end.isValid()) return 'months';
  const diffDays = end.diff(start, 'day');
  return diffDays <= 31 ? 'weeks' : 'months';
}

/**
 * Returns the first day of the month (YYYY-MM-DD). Period is parsed as local time (no UTC shift).
 */
function getMonthKey(period: string): string {
  return dayjs(period, 'YYYY-MM-DD', true).startOf('month').format('YYYY-MM-DD');
}

/**
 * Returns the Monday of the week (ISO 8601) for a given date string, as YYYY-MM-DD.
 * Period is parsed as local time so "2025-11-01" stays November.
 */
function getWeekKey(period: string): string {
  const d = dayjs(period, 'YYYY-MM-DD', true);
  const day = d.day(); // 0 Sun, 1 Mon, ... 6 Sat
  const monday = d.subtract(day === 0 ? 6 : day - 1, 'day');
  return monday.format('YYYY-MM-DD');
}

function addDataPoints(a: PolicyBarChartDataPoint, b: PolicyBarChartDataPoint): PolicyBarChartDataPoint {
  return {
    period: a.period,
    lexipolUpdates: {
      pendingCount: a.lexipolUpdates.pendingCount + b.lexipolUpdates.pendingCount,
      processedCount: a.lexipolUpdates.processedCount + b.lexipolUpdates.processedCount,
    },
    assignmentsCompleted: {
      overdueByWeekOrMore: a.assignmentsCompleted.overdueByWeekOrMore + b.assignmentsCompleted.overdueByWeekOrMore,
      overdueByLessThanWeek:
        a.assignmentsCompleted.overdueByLessThanWeek + b.assignmentsCompleted.overdueByLessThanWeek,
      onTime: a.assignmentsCompleted.onTime + b.assignmentsCompleted.onTime,
    },
    assignmentsNotCompleted: {
      overdueByWeekOrMore:
        a.assignmentsNotCompleted.overdueByWeekOrMore + b.assignmentsNotCompleted.overdueByWeekOrMore,
      overdueByLessThanWeek:
        a.assignmentsNotCompleted.overdueByLessThanWeek + b.assignmentsNotCompleted.overdueByLessThanWeek,
    },
  };
}

/**
 * Groups API metrics by period mode. When the API returns daily (or finer) data,
 * aggregates into monthly or weekly buckets: period = first day of month/week,
 * numeric values summed for that bucket.
 */
export function groupPolicyTrendsByPeriod(
  metrics: PolicyBarChartDataPoint[],
  periodMode: PeriodMode,
): PolicyBarChartDataPoint[] {
  if (!metrics.length) return [];

  const getKey = periodMode === 'months' ? getMonthKey : getWeekKey;
  const map = new Map<string, PolicyBarChartDataPoint>();

  for (const point of metrics) {
    const key = getKey(point.period);
    const existing = map.get(key);
    const withPeriod = { ...point, period: key };
    map.set(key, existing ? addDataPoints(existing, withPeriod) : withPeriod);
  }

  const sorted = Array.from(map.values()).sort((a, b) => a.period.localeCompare(b.period));
  return sorted;
}

export const getPolicyTrends = async (
  parentOrgId: string,
  childOrgId: string,
  queryParams: QueryParams,
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void,
): Promise<PolicyTrendsResponse> => {
  const url = `${baseApiUrl()}/activity-center/v1/organizations/${parentOrgId}/risk-pool/related-organizations/${childOrgId}/policy/policy-trends`;

  const params = new URLSearchParams();
  if (queryParams.startDate) params.append('startDate', queryParams.startDate);
  if (queryParams.endDate) params.append('endDate', queryParams.endDate);

  const queryString = params.toString();
  const fullUrl = queryString ? `${url}?${queryString}` : url;

  try {
    const data: PolicyTrendsResponse = await fetcher({
      method: 'get',
      url: fullUrl,
      options: { signal },
    });

    return data;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    }
    throw error;
  }
};

export default function usePolicyTrends(
  childOrgId: string,
  queryParams: QueryParams,
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void,
) {
  const { currentOrganizationId } = useContext(AppContext);
  const parentOrgId = currentOrganizationId || childOrgId;
  const isDemoDataEnabled = useRiskPoolDemoData();

  return useQuery<PolicyTrendsResponse, Error>(
    [
      'risk_pool_org_policy_trends',
      parentOrgId,
      childOrgId,
      queryParams.startDate,
      queryParams.endDate,
      queryParams.manualId,
    ],
    async ({ signal }): Promise<PolicyTrendsResponse> => {
      try {
        if (isDemoDataEnabled) {
          const generated = generatePolicyTrends(childOrgId, queryParams.startDate, queryParams.endDate);
          return {
            pendingUpdates: generated.metrics.map((m) => ({
              date: m.period,
              count: m.lexipolUpdates.pendingCount,
            })),
          };
        }

        return await getPolicyTrends(parentOrgId, childOrgId, queryParams, signal, onError);
      } catch (err) {
        if (onError) onError(err as ServerErrorResponse);
        throw err;
      }
    },
    {
      ...options,
      enabled: !!childOrgId && !!queryParams.startDate && !!queryParams.endDate,
    },
  );
}
